import React, {useState} from 'react';

import { useForm, Controller } from "react-hook-form";
import cx from "classnames";

import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Form from 'react-bootstrap/Form';

import { TextField } from "@mui/material";

import ButtonPrimary from "../../../components/button-primary/ButtonPrimary";

import styles from './ContactForm.module.scss';
import {COLORS} from "../../../config/constants";

type ContactFormProps = {
    isModal?: boolean;
    onSuccess: (isSuccess: boolean) => void;
    onError: (isError: boolean) => void;
    hasComment?: boolean;
};

function ContactForm({isModal, onSuccess, onError, hasComment}: ContactFormProps) {

    const [isSending, setIsSending] = useState(false);

    const {
        control,
        handleSubmit,
        reset,
        formState: { errors },
    } = useForm({
        mode: "onBlur",
    });

    const onSubmit = async (data) => {
        setIsSending(true);

        try {
            const formData = new URLSearchParams();
            for (const key in data.DATA) {
                formData.append(`DATA[${key}]`, data.DATA[key]);
            }

            const response = await fetch('/api/contact', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8'
                },
                body: formData.toString()
            });

            if (response.ok) {
                onSuccess(true);
                onError(false);
                reset();
            } else {
                onError(true);
                onSuccess(false);
            }
        } catch (error) {
            onError(true);
            onSuccess(false);
        } finally {
            setIsSending(false)
        }

    };

    return (
        <Row className="justify-content-center">
            <Col xl={isModal ? 12 : 6}>
                <Form onSubmit={handleSubmit(onSubmit)}>
                    <Row>
                        <Controller
                            name="DATA[TITLE]"
                            control={control}
                            defaultValue="Заявка с сайта"
                            render={({ field }) => (
                                <TextField
                                    {...field}
                                    hidden
                                />
                            )}
                        />
                        <Col className={cx({
                            [styles.inputContainerBig]: !isModal,
                            [styles.inputContainerSmall]: isModal,
                        })} xs={12} md={12}>
                            <Controller
                                name="DATA[NAME]"
                                control={control}
                                defaultValue=""
                                rules={{
                                    required: "Поле обязательно для заполнения",
                                }}
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        label="Имя"
                                        variant="outlined"
                                        error={!!errors.DATA?.['NAME']}
                                        helperText={errors.DATA?.['NAME']?.message}
                                        fullWidth
                                        className={cx({
                                            [styles.isLightMode]: isModal,
                                        })}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                backgroundColor: isModal ? 'transparent' : COLORS.BLACK,
                                                '&.Mui-focused': {
                                                    borderWidth: '2px',
                                                },
                                                '&.Mui-error': {
                                                    border:  `1px solid ${isModal ? COLORS.RED_LIGHT : COLORS.RED_DARK}`,
                                                },
                                            },
                                        }}
                                    />
                                )}
                            />
                        </Col>
                        <Col className={styles.inputContainerSmall} xs={12} md={12}>
                            <Controller
                                name="DATA[EMAIL]"
                                control={control}
                                defaultValue=""
                                rules={{
                                    required: "Поле обязательно для заполнения",
                                    pattern: {
                                        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                                        message: "Неправильный e-mail.",
                                    },
                                }}
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        label="Электронная почта"
                                        variant="outlined"
                                        error={!!errors.DATA?.['EMAIL']}
                                        helperText={errors.DATA?.['EMAIL']?.message}
                                        fullWidth
                                        className={cx({
                                            [styles.isLightMode]: isModal,
                                        })}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                backgroundColor: isModal ? 'transparent' : COLORS.BLACK,
                                                '&.Mui-focused': {
                                                    borderWidth: '2px',
                                                },
                                                '&.Mui-error': {
                                                    border:  `1px solid ${isModal ? COLORS.RED_LIGHT : COLORS.RED_DARK}`,
                                                },
                                            },
                                        }}
                                    />
                                )}
                            />
                        </Col>
                    </Row>
                    {hasComment && (
                        <Row>
                            <Col xs={12} className={styles.textareaContainerBig}>
                                <Controller
                                    name="DATA[COMMENTS]"
                                    control={control}
                                    defaultValue=""
                                    render={({ field }) => (
                                        <TextField
                                            {...field}
                                            label="Вопросы, предложения"
                                            variant="outlined"
                                            error={!!errors.DATA?.['COMMENTS']}
                                            helperText={errors.DATA?.['COMMENTS']?.message}
                                            fullWidth
                                            multiline
                                            rows={3}
                                            className={cx({
                                                [styles.isLightMode]: isModal,
                                            })}
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    backgroundColor: isModal ? 'transparent' : COLORS.BLACK,
                                                    '&.Mui-focused': {
                                                        borderWidth: '2px',
                                                    },
                                                    '&.Mui-error': {
                                                        border:  `1px solid ${isModal ? COLORS.RED_LIGHT : COLORS.RED_DARK}`,
                                                    },
                                                },
                                            }}
                                        />
                                    )}
                                />
                            </Col>
                        </Row>
                    )}
                    <Row>
                        <Col lg={{ span: isModal ? 6 : 12, offset: isModal ? 6 : 0 }}>
                            <ButtonPrimary
                                type="submit"
                                className={cx(styles.buttonSubmit, {
                                [styles.buttonModal]: isModal,
                            })}
                                disabled={isSending}
                            >
                                <>
                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M2.01 21L23 12L2.01 3L2 10L17 12L2 14L2.01 21Z" fill="currentColor"/>
                                    </svg>
                                    {isModal && !hasComment ? 'Оставить заявку': 'Отправить'}
                                </>
                            </ButtonPrimary>
                        </Col>
                    </Row>
                </Form>
            </Col>
        </Row>
    );
}

export default ContactForm;
